import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Field,
  Spinner,
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
  TagPickerProps
} from '@fluentui/react-components';

import { AvailableMedicalService } from '../../../../models/Entities/MedicalServices/AvailableMedicalService';
import * as MedicalServicesService from '../../../../services/MedicalServices.Services';

// lógica
import {
  MIN_QUERY_LENGTH,
  SEARCH_DEBOUNCE_MS,
  SUGGESTIONS_PAGE_SIZE,
  buildOptions,
  mergeServices,
  optionLabel,
  resolveSelected,
  shouldSearch
} from './MedicalServiceSelector.Logic';

import * as ownFunctions from './MedicalServiceSelectorFunctions';
import './MedicalServiceSelector.Styles.css';

/**
 * Selector de servicios médicos con búsqueda en servidor. No necesita el catálogo completo en
 * redux: con dos letras pide al API los servicios que coinciden (nombre o código interno). Si
 * otra pantalla ya cargó el catálogo, se usa como sugerencias iniciales y filtro instantáneo.
 * Los ids preseleccionados que no estén en memoria se traen por id para mostrar su nombre.
 */
const MedicalServiceSelectorComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<AvailableMedicalService[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);
  const [searchFailed, setSearchFailed] = useState<boolean>(false);
  const [selected, setSelected] = useState<AvailableMedicalService[]>([]);
  const [loadingSelected, setLoadingSelected] = useState<boolean>(false);
  const lastSearched = useRef<string | undefined>(undefined);
  const searchSeq = useRef<number>(0);

  // Todo lo que conocemos: catálogo (si está), resultados de búsqueda y lo ya elegido.
  const pool = useMemo(() => mergeServices(props.CatalogServices, results, selected), [props.CatalogServices, results, selected]);

  // Ids preseleccionados → servicios; los que no están en memoria se piden por id.
  useEffect(() => {
    const ids = props.MedicalServicesSelectedIds ?? [];
    const { found, missing } = resolveSelected(ids, pool);
    if (ids.length === 0) {
      if (selected.length > 0) setSelected([]);
      return;
    }
    if (missing.length === 0) {
      if (found.map((item) => item.id).join(',') !== selected.map((item) => item.id).join(',')) setSelected(found);
      return;
    }
    if (!props.access_token) return;
    let cancelled = false;
    setLoadingSelected(true);
    Promise.all(missing.map((id) => MedicalServicesService.GetMedicalServiceById(id, props.access_token as string).catch(() => undefined)))
      .then((fetched) => {
        if (cancelled) return;
        const extra = fetched.filter((item): item is AvailableMedicalService => !!item);
        setSelected(resolveSelected(ids, mergeServices(pool, extra)).found);
      })
      .finally(() => { if (!cancelled) setLoadingSelected(false); });
    return () => { cancelled = true; };
  }, [props.MedicalServicesSelectedIds, props.CatalogServices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Búsqueda en servidor con espera tras la última tecla.
  useEffect(() => {
    if (!shouldSearch(query, lastSearched.current)) {
      if (query.trim().length < MIN_QUERY_LENGTH) { setResults([]); setHasMore(false); lastSearched.current = undefined; }
      return;
    }
    if (!props.access_token) return;
    const text = query.trim();
    const timer = setTimeout(() => {
      const seq = ++searchSeq.current;
      setSearching(true);
      setSearchFailed(false);
      MedicalServicesService.GetMedicalServicesPage({ pageNumber: 1, pageSize: SUGGESTIONS_PAGE_SIZE, searchText: text }, props.access_token as string)
        .then((page) => {
          if (seq !== searchSeq.current) return;
          lastSearched.current = text;
          setResults(page.data ?? []);
          setHasMore((page.totalRecords ?? 0) > (page.data ?? []).length);
        })
        .catch(() => { if (seq === searchSeq.current) { setSearchFailed(true); setResults([]); } })
        .finally(() => { if (seq === searchSeq.current) setSearching(false); });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, props.access_token]);

  const selectedIds = selected.map((item) => item.id);
  const hasQuery = query.trim().length >= MIN_QUERY_LENGTH;
  // Con texto: lo que dijo el servidor (más el catálogo filtrado, si está). Sin texto: catálogo.
  const candidates = hasQuery ? mergeServices(props.CatalogServices, results) : props.CatalogServices;
  const options = buildOptions(candidates, selectedIds, query);

  const emit = (next: AvailableMedicalService[]) => {
    setSelected(next);
    props.onSelectedServices?.(next.length > 0 ? next : undefined);
  };

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (_event, data) => {
    if (data.selectedOptions.length === 0) {
      // En selección simple Fluent dispara esto al abrir la lista: la quita sólo la X de la etiqueta.
      if (!props.MultiselectEnable) return;
      emit([]);
      return;
    }
    const pickedId = Number(data.value);
    const picked = pool.find((item) => item.id === pickedId);
    if (!picked) return;
    if (props.MultiselectEnable) {
      const next = data.selectedOptions.map((value) => pool.find((item) => item.id === Number(value))).filter((item): item is AvailableMedicalService => !!item);
      emit(next);
    } else {
      emit([picked]);
    }
    setQuery('');
  };

  const removeTag = (id: number) => emit(props.MultiselectEnable ? selected.filter((item) => item.id !== id) : []);

  let listContent: React.ReactNode;
  if (searching) {
    listContent = <div role="status" aria-live="polite" className="mss-note"><Spinner size="tiny" /> {t('medicalServiceSelector.searching')}</div>;
  } else if (searchFailed) {
    listContent = <div role="alert" className="mss-note mss-note--error">{t('medicalServiceSelector.error')}</div>;
  } else if (options.length > 0) {
    listContent = (
      <>
        {options.map((service) => (
          <TagPickerOption key={service.id} value={String(service.id)} text={optionLabel(service)}>
            {optionLabel(service)}
          </TagPickerOption>
        ))}
        {hasMore && <div role="note" className="mss-note">{t('medicalServiceSelector.more')}</div>}
      </>
    );
  } else if (hasQuery) {
    listContent = <div role="note" aria-live="polite" className="mss-note">{t('medicalServiceSelector.noResults', { query: query.trim() })}</div>;
  } else {
    listContent = <div role="note" className="mss-note">{t('medicalServiceSelector.hintType', { min: MIN_QUERY_LENGTH })}</div>;
  }

  return (
    <>
      <Field label={props.label ?? t('medicalServiceSelector.label')} hint={props.noteText}>
        {loadingSelected && (
          <div role="status" aria-live="polite" className="mss-note"><Spinner size="tiny" /> {t('medicalServiceSelector.loadingSelected')}</div>
        )}
        <TagPicker
          onOptionSelect={onOptionSelect}
          selectedOptions={selectedIds.map(String)}
          disabled={props.disabled}
        >
          <TagPickerControl>
            <TagPickerGroup aria-label={t('medicalServiceSelector.selectedGroup')}>
              {selected.map((service) => (
                <Tag
                  key={service.id}
                  shape="rounded"
                  value={String(service.id)}
                  dismissIcon={{
                    'aria-label': t('medicalServiceSelector.remove'),
                    onClick: (event: React.MouseEvent) => { event.stopPropagation(); removeTag(service.id); }
                  }}
                >
                  {optionLabel(service)}
                </Tag>
              ))}
            </TagPickerGroup>
            <TagPickerInput
              aria-label={props.label ?? t('medicalServiceSelector.label')}
              placeholder={selected.length === 0 || props.MultiselectEnable ? t('medicalServiceSelector.placeholder') : undefined}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              disabled={props.disabled || (!props.MultiselectEnable && selected.length > 0)}
              data-testid="medical-service-selector-input"
            />
          </TagPickerControl>
          <TagPickerList>{listContent}</TagPickerList>
        </TagPicker>
      </Field>
    </>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(MedicalServiceSelectorComponent as any);
